import { useQuery } from '@tanstack/react-query';
import { Bus } from 'lucide-react';
import { Card } from '@/components/common/Card';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States';
import {
  EnrollmentStatusBadge,
  TripStatusBadge,
  delayText,
  formatTime,
  humanise,
} from '@/features/transport/shared';
import { portalApi } from '@/services/api/endpoints/portal';
import { queryKeys } from '@/services/api/queryKeys';
import { formatDate } from '@/utils/format';
import { PortalShell, type PortalContext } from './PortalShell';
import { SectionTitle } from './shared';

/**
 * The school bus, from the family's side: which service the student is on, and
 * today's runs on those routes. Not /my/transport — that is the driver's page.
 */
export function PortalBusPage() {
  return (
    <PortalShell title="School bus" icon={<Bus className="h-5 w-5" aria-hidden="true" />}>
      {(context) => <BusView context={context} />}
    </PortalShell>
  );
}

function BusView({ context }: { context: PortalContext }) {
  const { subject, subjectKey } = context;

  const transport = useQuery({
    queryKey: queryKeys.portal.transport(subjectKey),
    queryFn: () => portalApi.transport(subject),
  });

  if (transport.isPending) {
    return (
      <Card>
        <LoadingState label="Loading transport" />
      </Card>
    );
  }
  if (transport.isError) {
    return (
      <Card>
        <ErrorState error={transport.error} onRetry={() => void transport.refetch()} />
      </Card>
    );
  }

  const { enrollments, tripsToday } = transport.data;

  if (enrollments.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<Bus className="h-6 w-6" aria-hidden="true" />}
          title="Not on the school bus"
          description="There is no transport enrolment on record. The transport office can arrange one."
        />
      </Card>
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <SectionTitle>Bus service</SectionTitle>
        <ul className="divide-y divide-line">
          {enrollments.map((enrollment) => (
            <li key={enrollment.id} className="px-4 py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-semibold text-ink">
                  {enrollment.academicYearName ?? 'Transport'} · {humanise(enrollment.direction)}
                </span>
                <EnrollmentStatusBadge status={enrollment.status} />
              </div>
              <dl className="mt-2 grid gap-2 text-sm sm:grid-cols-2">
                {enrollment.pickupRouteName !== undefined && (
                  <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wider text-subtle">Morning pickup</dt>
                    <dd className="text-ink">
                      {enrollment.pickupRouteName}
                      {enrollment.pickupStopName !== undefined && ` · ${enrollment.pickupStopName}`}
                    </dd>
                  </div>
                )}
                {enrollment.dropOffRouteName !== undefined && (
                  <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wider text-subtle">Afternoon drop-off</dt>
                    <dd className="text-ink">
                      {enrollment.dropOffRouteName}
                      {enrollment.dropOffStopName !== undefined && ` · ${enrollment.dropOffStopName}`}
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-[11px] font-semibold uppercase tracking-wider text-subtle">Since</dt>
                  <dd className="text-ink">
                    {formatDate(enrollment.startDate)}
                    {enrollment.endDate !== undefined && ` until ${formatDate(enrollment.endDate)}`}
                  </dd>
                </div>
              </dl>
              {enrollment.statusReason !== undefined && (
                <p className="mt-2 text-xs text-muted">{enrollment.statusReason}</p>
              )}
              {enrollment.specialInstructions !== undefined && (
                <p className="mt-2 text-xs text-muted">Note: {enrollment.specialInstructions}</p>
              )}
            </li>
          ))}
        </ul>
      </Card>

      <Card>
        <SectionTitle>Today’s trips</SectionTitle>
        {tripsToday.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted">No trips on these routes today.</p>
        ) : (
          <ul className="divide-y divide-line">
            {tripsToday.map((trip) => (
              <li key={trip.id} className="flex flex-wrap items-center gap-3 px-4 py-2.5">
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-ink">
                    {trip.routeName} · {humanise(trip.direction)}
                  </span>
                  <span className="block text-[11px] text-subtle">
                    {formatTime(trip.plannedStartTime)}–{formatTime(trip.plannedEndTime)}
                    {trip.vehicleName !== undefined && ` · ${trip.vehicleName}`}
                    {trip.driverName !== undefined && ` · ${trip.driverName}`}
                  </span>
                  {trip.status === 'DELAYED' && (
                    <span className="block text-[11px] font-semibold text-warning">
                      {delayText(trip.delayMinutes)}
                      {trip.delayReason !== undefined && ` · ${trip.delayReason}`}
                    </span>
                  )}
                  {trip.status === 'CANCELLED' && trip.cancellationReason !== undefined && (
                    <span className="block text-[11px] font-semibold text-danger">
                      {trip.cancellationReason}
                    </span>
                  )}
                </span>
                <TripStatusBadge status={trip.status} />
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
